import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Barbell, ForkKnife, Target, Snowflake, ArrowRight, Trophy, Lightning, Flame, UsersThree } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [progress, setProgress] = useState([]);
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [matchStatus, setMatchStatus] = useState(null);

  useEffect(() => {
    api.get("/goals").then((r) => setGoals(r.data)).catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/coach/my-notes").then((r) => setNotes(r.data)).catch(() => {});
    api.get("/sessions/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/match-week/status").then((r) => setMatchStatus(r.data)).catch(() => {});
  }, []);

  const tier = user?.subscription_tier || "free";
  const isFree = tier === "free";

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Dashboard</div>
            <h1 className="font-heading font-black uppercase text-5xl md:text-6xl mt-2">Welcome, {user?.name?.split(" ")[0]}</h1>
            <p className="text-[#A3A3A3] mt-2">
              {user?.profile ? `${user.profile.position} · ${user.profile.current_level.replace("_", " ")} → ${user.profile.aspirational_level.replace("_", " ")}` : "Set up your profile to unlock personalised plans."}
            </p>
          </div>
          {!user?.profile && (
            <Link to="/profile" data-testid="setup-profile-cta"
              className="border border-[#FF3B30] bg-[#FF3B30]/10 px-6 py-3 font-heading uppercase tracking-widest text-sm text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white">
              Setup Profile
            </Link>
          )}
        </div>

        {matchStatus?.active && (
          <div className="mb-6 border-l-4 border-[#FF3B30] bg-[#FF3B30]/10 p-5 flex items-center justify-between" data-testid="match-week-banner">
            <div className="flex items-center gap-4">
              <Flame size={32} weight="fill" className="text-[#FF3B30]" />
              <div>
                <div className="font-heading font-black uppercase text-xl">Match Week Active</div>
                <div className="text-sm text-[#A3A3A3]">
                  {matchStatus.auto_active ? `${matchStatus.days_out} days out from match. Volume tapered, intensity preserved.` : "Manual mode: taper enabled."}
                </div>
              </div>
            </div>
            <Link to="/gym-plan" className="border border-[#FF3B30] bg-[#FF3B30] text-white px-5 py-2 font-heading uppercase tracking-widest text-xs" data-testid="view-taper">View Taper</Link>
          </div>
        )}

        {isFree && (
          <div className="mb-8 border border-[#FF3B30] bg-[#FF3B30]/10 p-6 flex items-center justify-between" data-testid="upgrade-banner">
            <div>
              <div className="font-heading uppercase font-bold text-lg">You're on the Free Plan</div>
              <div className="text-sm text-[#A3A3A3] mt-1">Upgrade to unlock AI-generated plans, all recovery tips, and unlimited goals.</div>
            </div>
            <Link to="/pricing" className="border border-[#FF3B30] bg-[#FF3B30] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm hover:bg-[#D63026]" data-testid="banner-upgrade">
              Upgrade
            </Link>
          </div>
        )}

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Streak" value={stats?.streak_days ?? 0} unit="days" icon={Flame} highlight />
          <StatCard label="This Week" value={stats?.this_week ?? 0} unit="sessions" icon={Lightning} />
          <StatCard label="Active Goals" value={goals.filter((g) => g.status === "active").length} icon={Target} />
          <StatCard label="Last 30d" value={stats?.last_30_days ?? 0} unit="sessions" icon={Trophy} />
        </div>

        {/* MAIN GRID */}
        <div className="grid md:grid-cols-2 gap-4">
          <ModuleCard to="/gym-plan" title="Gym Plan" icon={Barbell} desc={isFree ? "Sample workout available. Upgrade for full AI plan." : "Your AI-generated weekly programme."} testid="module-gym" />
          <ModuleCard to="/meal-plan" title="Meal Plan" icon={ForkKnife} desc={isFree ? "Sample meal available. Upgrade for full macros." : "Daily fuel dialled to your goals."} testid="module-meal" />
          <ModuleCard to="/recovery" title="Recovery" icon={Snowflake} desc="Sleep, ice, mobility protocols." testid="module-recovery" />
          <ModuleCard to="/goals" title="Goals" icon={Target} desc={`${goals.filter((g) => g.status === "active").length} active`} testid="module-goals" />
          <ModuleCard to="/progress" title="Progress" icon={Lightning} desc={`${progress.length} entries logged`} testid="module-progress" />
          <ModuleCard to="/matches" title="Matches" icon={Trophy} desc="Log fixtures — AI learns from them" testid="module-matches" />
          <ModuleCard to="/team" title="Team" icon={UsersThree} desc="Squad hub" testid="module-team" />
        </div>

        {notes.length > 0 && (
          <div className="mt-8 border border-[#262626] bg-[#121212] p-6">
            <h2 className="font-heading font-bold uppercase text-lg mb-4 accent-underline inline-block">Coach Notes</h2>
            <div className="space-y-3">
              {notes.slice(0, 3).map((n) => (
                <div key={n.id} className="border-l-2 border-[#FF3B30] pl-4 py-2" data-testid={`coach-note-${n.id}`}>
                  <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">{new Date(n.created_at).toLocaleDateString()}</div>
                  <div className="text-sm mt-1">{n.content}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, unit, icon: Icon, highlight }) {
  return (
    <div className={`border p-6 ${highlight ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-[#262626] bg-[#121212]"}`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={20} weight={highlight ? "fill" : "duotone"} className="text-[#FF3B30] mb-3" />
      <div className="font-heading font-black text-4xl">{value}</div>
      <div className="text-xs uppercase tracking-widest text-[#A3A3A3] mt-1">{unit ? `${unit} · ${label}` : label}</div>
    </div>
  );
}

function ModuleCard({ to, title, icon: Icon, desc, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="group border border-[#262626] bg-[#121212] p-8 hover:border-[#FF3B30] hover:bg-[#1C1C1C] transition-all">
      <div className="flex items-start justify-between">
        <Icon size={44} weight="duotone" className="text-[#FF3B30]" />
        <ArrowRight size={20} className="text-[#A3A3A3] group-hover:text-[#FF3B30] group-hover:translate-x-1 transition-all" />
      </div>
      <div className="mt-6 font-heading font-bold uppercase text-3xl">{title}</div>
      <div className="text-sm text-[#A3A3A3] mt-2">{desc}</div>
    </Link>
  );
}
